import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import type { CatalogItem } from '../../../../redux/api/fymAPI.types';
import type { SongPageSection, SongLyricsView } from '../../../smartLinks/catalog/song/page/catalogSongPath';

/** The master of a song as Releases and the Studio show it. They never upload or replace it. */
export type MasterFact = 'none' | 'processing' | 'ready' | 'failed';

/** The lyrics of a song: none, a text with no times, a sync that runs, synced, or a failed sync. */
export type LyricsFact = 'none' | 'plain' | 'syncing' | 'synced' | 'failed';

/** What the song page in Catalog must add for the kit: the release date and the cover art. */
export interface DetailsFact {
  noDate: boolean;
  noCover: boolean;
}

/** Where a button of the song field goes: a section of the song page in Catalog. */
export interface SongPageTarget {
  section: SongPageSection;
  view?: SongLyricsView;
}

/**
 * The master state of the row. `refusedNoMaster`: the server answered `M24_NO_MASTER`, so the row
 * lost its master after the list was read.
 */
export function masterFactOf(item: CatalogItem, refusedNoMaster = false): MasterFact {
  if (refusedNoMaster || !item.song.hasMaster) {
    return 'none';
  }
  return item.song.masterStatus ?? 'none';
}

/** The lyric state of the row. An old answer with no sync state counts the lyrics as plain text. */
export function lyricsFactOf(item: CatalogItem): LyricsFact {
  if (!item.song.hasLyrics) {
    return 'none';
  }
  switch (item.song.lyricSyncStatus) {
    case 'syncing':
      return 'syncing';
    case 'synced':
      return 'synced';
    case 'failed':
      return 'failed';
    case 'none':
    case undefined:
      return 'plain';
  }
}

/**
 * The details that the kit needs and that the row does not have. Only an unreleased row has a date
 * and a cover art to add: a claimed row takes both from the services.
 */
export function detailsFactOf(item: CatalogItem): DetailsFact {
  if (item.kind !== CatalogItemKind.unreleased) {
    return { noDate: false, noCover: false };
  }
  return {
    noDate: item.releaseDate === null,
    noCover: item.artworkFile === null && item.artworkUrl === null,
  };
}

/** The section of the song page that fixes the master, or null when nothing is to fix. */
export function masterTarget(fact: MasterFact): SongPageTarget | null {
  return fact === 'none' || fact === 'failed' ? { section: 'files' } : null;
}

/**
 * The section of the song page that fixes the lyrics, or null when nothing is to fix. No lyrics
 * opens the Text view; a failed sync opens the times.
 */
export function lyricsTarget(fact: LyricsFact): SongPageTarget | null {
  if (fact === 'none') {
    return { section: 'lyrics', view: 'text' };
  }
  return fact === 'failed' ? { section: 'lyrics' } : null;
}

/** The section of the song page that fixes the details, or null when nothing is to fix. */
export function detailsTarget(fact: DetailsFact): SongPageTarget | null {
  return fact.noDate || fact.noCover ? { section: 'details' } : null;
}
